<script lang="ts">
  /**
   * Hindsight's screen. The thing v1 deliberately did not build.
   *
   * Everything here reads the **rollups**, not `state_raw`. That is not a
   * preference: raw is partitioned by day with `require_partition_filter = TRUE`
   * specifically so an unbounded query fails rather than quietly scanning years
   * and billing for it, and a month view of forty entities drawn from raw would
   * scan every partition it spans on every page load. `daily_entity` is a few
   * hundred rows a day, so the same view is nearly free.
   *
   * The one exception is *today*, which has no rollup by design — a partial day in
   * a rollup table shows as a dip every morning and nobody knows it is an
   * artefact. Today is fetched from raw only when someone opens a single entity,
   * scoped to one partition, and the panel says so.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";

  type Summary = {
    rollupsMissing: boolean;
    firstDay: string | null;
    lastDay: string | null;
    daysCovered: number;
    entities: number;
    daily: { day: string; entities: number; rows: number; silent: number }[];
  };
  type EntityRow = { entityId: string; domain: string | null; unit: string | null; lastDay: string | null; days: number; samples: number };
  type Point = { day: string; samples: number; min: number | null; max: number | null; avg: number | null; last: number | null; onHours: number | null; changes: number };
  type Reading = { at: string; state: string | null; num: number | null };
  type Finding = { rule: string; text: string; at: string };
  type Ranked = { entityId: string; domain: string | null; unit: string | null; samples: number; days: number; avg: number | null; onHours: number | null };
  type Leaderboard = {
    days: number;
    total: number;
    totalSamples: number;
    busiest: Ranked[];
    quietest: Ranked[];
    byDomain: { domain: string; entities: number; samples: number }[];
  };
  type Compare = {
    entityId: string;
    days: number;
    current: { avg: number | null; onHours: number | null; samples: number };
    previous: { avg: number | null; onHours: number | null; samples: number };
    change: { avgPct: number | null; onHoursPct: number | null };
  };

  type Panel<T> = { data: T | null; error: string | null; loading: boolean };

  const summary = $state<Panel<Summary>>({ data: null, error: null, loading: true });
  const entities = $state<Panel<{ entities: EntityRow[] }>>({ data: null, error: null, loading: true });
  const series = $state<Panel<{ entity: string; days: number; series: Point[] }>>({ data: null, error: null, loading: false });
  const today = $state<Panel<{ entity: string; readings: Reading[] }>>({ data: null, error: null, loading: false });
  const drift = $state<Panel<{ findings: Finding[] }>>({ data: null, error: null, loading: true });
  const board = $state<Panel<Leaderboard>>({ data: null, error: null, loading: true });
  const compare = $state<Panel<Compare>>({ data: null, error: null, loading: false });

  let selected = $state<string | null>(null);
  let days = $state(30);
  let filter = $state("");
  let showToday = $state(false);

  async function fill<T>(p: Panel<T>, path: string) {
    p.loading = true;
    try {
      p.data = await api.get<T>(path);
      p.error = null;
    } catch (e) {
      p.error = e instanceof ApiError ? e.message : "Unavailable";
    } finally {
      p.loading = false;
    }
  }

  $effect(() => {
    void fill(summary, "/summary");
    void fill(entities, "/entities");
    void fill(drift, "/drift");
    void fill(board, "/leaderboard?days=30");
  });

  async function pick(id: string) {
    selected = id;
    showToday = false;
    await Promise.all([
      fill(series, `/series?entity=${encodeURIComponent(id)}&days=${days}`),
      fill(compare, `/compare?entity=${encodeURIComponent(id)}&days=${days}`),
    ]);
  }

  async function setDays(d: number) {
    days = d;
    if (!selected) return;
    await Promise.all([
      fill(series, `/series?entity=${encodeURIComponent(selected)}&days=${days}`),
      fill(compare, `/compare?entity=${encodeURIComponent(selected)}&days=${days}`),
    ]);
  }

  /** A signed percentage, or an em dash when there is no baseline to compare to. */
  const pct = (v: number | null) => (v === null ? "—" : `${v > 0 ? "+" : ""}${v}%`);

  async function loadToday() {
    if (!selected) return;
    showToday = true;
    await fill(today, `/today?entity=${encodeURIComponent(selected)}`);
  }

  const shown = $derived(
    (entities.data?.entities ?? []).filter((e) => !filter || e.entityId.toLowerCase().includes(filter.toLowerCase())),
  );

  /**
   * The metric to plot. A numeric sensor plots its daily average; an on/off device
   * plots hours on. Choosing per entity rather than offering a picker means the
   * chart is right by default — and for a switch, "average of 0 and 1" is a number
   * that means nothing.
   */
  const points = $derived.by(() => {
    const s = series.data?.series ?? [];
    const numeric = s.some((p) => p.avg !== null);
    return s.map((p) => ({ day: p.day, value: numeric ? p.avg : p.onHours, raw: p }));
  });
  /**
   * `sensor.victron_solar_power` → "Victron solar power".
   *
   * The raw id is what the database calls it; it is not what a person calls it,
   * and a column of them is the single biggest reason this screen read as a
   * database viewer. The id is kept, one size down, because it is what you need
   * when writing a query or an allowlist entry — it just should not be the thing
   * the eye lands on first.
   */
  function nice(entityId: string): string {
    const bare = entityId.replace(/^[a-z_]+\./, "").replace(/_/g, " ");
    return bare.charAt(0).toUpperCase() + bare.slice(1);
  }

  const isNumeric = $derived((series.data?.series ?? []).some((p) => p.avg !== null));
  const peak = $derived(Math.max(...points.map((p) => p.value ?? 0), 0));

  const fmt = (v: number | null, unit: string | null) =>
    v == null ? "—" : `${Math.abs(v) >= 100 ? Math.round(v) : v.toFixed(1)}${unit ? ` ${unit}` : ""}`;

  const unitOf = $derived(entities.data?.entities.find((e) => e.entityId === selected)?.unit ?? null);
</script>

<div class="wrap">
  <!-- ── Coverage ──────────────────────────────────────────────────────── -->
  <section class="card--hero hero">
    <div class="kicker">Hindsight</div>
    <h1>What the house remembers.</h1>
    <p class="sub hero__p">
      Home Assistant's recorder purges at 30 days. Everything below is history that would
      otherwise be gone.
    </p>

    {#if summary.loading}
      <p class="sub pad">Loading…</p>
    {:else if summary.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {summary.error}</p>
    {:else if summary.data?.rollupsMissing}
      <!-- Honest, and specific about the remedy. -->
      <p class="sub pad">
        <Icon name="pause" size={12} />
        No rollups yet. The 03:00 job builds them — run <code>POST /rollups/daily?days=30</code> to
        backfill, and this fills in.
      </p>
    {:else if summary.data}
      {@const s = summary.data}
      <div class="figs">
        <div class="fig">
          <div class="big">{s.daysCovered}<span class="unit">days</span></div>
          <div class="lb">history kept</div>
        </div>
        <div class="fig">
          <div class="big">{s.entities}</div>
          <div class="lb">entities</div>
        </div>
        <div class="fig">
          <div class="big">{s.daily.reduce((n, d) => n + d.rows, 0).toLocaleString("en-ZA")}</div>
          <div class="lb">readings, 30 days</div>
        </div>
      </div>
      <p class="sub foot">
        {s.firstDay ? `${dateZA(s.firstDay)} to ${dateZA(s.lastDay ?? s.firstDay)}` : "no rollups"}
      </p>

      {#if s.daily.some((d) => d.silent > 0)}
        <!-- The number worth watching: "40 entities reported" means nothing
             without "and 3 that usually do did not". -->
        <div class="attn attn--warn silent">
          <span class="attn__badge" aria-hidden="true"><Icon name="alert" size={12} /></span>
          <span class="attn__text">
            {s.daily[s.daily.length - 1]?.silent ?? 0} entities that normally report were silent on the
            most recent day.
          </span>
        </div>
      {/if}
    {/if}
  </section>

  <!-- ── Drift findings ────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">What the rules noticed</span></div>
    {#if drift.loading}
      <p class="sub pad">Loading…</p>
    {:else if drift.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {drift.error}</p>
    {:else if (drift.data?.findings ?? []).length === 0}
      <p class="sub pad">
        <Icon name="check" size={12} /> Nothing has fired. The three rules are deliberately
        conservative — one that cries wolf gets the whole morning message muted.
      </p>
    {:else}
      {#each drift.data!.findings as f (f.rule + f.at)}
        <div class="row row--warn">
          <Icon name="alert" size={14} />
          <div class="grow">
            <div class="row__t">{f.text}</div>
            <div class="sub"><span class="num">{f.rule}</span> · {dateZA(f.at)}</div>
          </div>
        </div>
      {/each}
    {/if}
  </section>

  <!-- ── What the house is doing ───────────────────────────────────────── -->
  <!-- Ranked by sample count, not by value: the values are in watts, litres,
       degrees and booleans, and adding them would be meaningless. Sample count is
       the one comparable measure across a heterogeneous estate, and it is also the
       one that predicts storage and spots a chatty sensor. -->
  <section class="card">
    <div class="sec">
      <span class="kicker">What the house is doing</span>
      <span class="sub">
        {#if board.data}{board.data.total} entities · {board.data.totalSamples.toLocaleString("en-ZA")} readings · {board.data.days}d{/if}
      </span>
    </div>

    {#if board.loading}
      <p class="sub pad">Loading…</p>
    {:else if board.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {board.error}</p>
    {:else if board.data && board.data.total === 0}
      <p class="sub pad"><Icon name="pause" size={12} /> No rolled-up days in this window yet.</p>
    {:else if board.data}
      <div class="domains">
        {#each board.data.byDomain as d (d.domain)}
          <span class="chip">{d.domain} <span class="sub num">{d.entities} · {d.samples.toLocaleString("en-ZA")}</span></span>
        {/each}
      </div>

      <div class="two">
        <div>
          <div class="colhead sub">Busiest</div>
          {#each board.data.busiest as e (e.entityId)}
            <button class="lb__row" onclick={() => pick(e.entityId)}>
              <span class="lb__text">
                <span class="lb__n">{nice(e.entityId)}</span>
                <span class="lb__id num">{e.entityId}</span>
              </span>
              <span class="sub num">{e.samples.toLocaleString("en-ZA")}</span>
            </button>
          {/each}
        </div>
        <div>
          <!-- A sensor reporting twice a month is either broken or genuinely quiet,
               and the two look identical until you ask. That is why this sits beside
               the busiest rather than being left out. -->
          <div class="colhead sub">Quietest — worth a look</div>
          {#each board.data.quietest as e (e.entityId)}
            <button class="lb__row" onclick={() => pick(e.entityId)}>
              <span class="lb__text">
                <span class="lb__n">{nice(e.entityId)}</span>
                <span class="lb__id num">{e.entityId}</span>
              </span>
              <span class="sub num">{e.samples.toLocaleString("en-ZA")} in {e.days}d</span>
            </button>
          {/each}
        </div>
      </div>
    {/if}
  </section>

  <!-- ── Entity picker + chart ─────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Entities</span>
      <span class="acts">
        {#each [7, 30, 90, 365] as d (d)}
          <button class="tab" class:tab--on={days === d} onclick={() => setDays(d)}>{d}d</button>
        {/each}
      </span>
    </div>

    {#if entities.loading}
      <p class="sub pad">Loading…</p>
    {:else if entities.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {entities.error}</p>
    {:else}
      <input class="search" bind:value={filter} placeholder="Filter entities…" aria-label="Filter entities" />

      <div class="split">
        <div class="list">
          {#each shown.slice(0, 200) as e (e.entityId)}
            <button class="ent" class:ent--on={selected === e.entityId} onclick={() => pick(e.entityId)}>
              <span class="ent__text">
                <span class="ent__n">{nice(e.entityId)}</span>
                <span class="ent__id num">{e.entityId}</span>
              </span>
              <span class="sub num">{e.days}d</span>
            </button>
          {/each}
          {#if shown.length > 200}
            <p class="sub pad">Showing 200 of {shown.length}. Filter to narrow.</p>
          {/if}
        </div>

        <div class="chart">
          {#if !selected}
            <p class="sub pad">Pick an entity.</p>
          {:else if series.loading}
            <p class="sub pad">Loading…</p>
          {:else if series.error}
            <p class="sub pad"><Icon name="alert" size={12} /> {series.error}</p>
          {:else if points.length === 0}
            <p class="sub pad">No rolled-up days for {selected} in this window.</p>
          {:else}
            <div class="chart__h">
              <span class="nm">{nice(selected)}</span>
              <span class="sub num chart__id">{selected}</span>
              <span class="sub">{isNumeric ? `daily average${unitOf ? ` (${unitOf})` : ""}` : "hours on per day"}</span>
            </div>

            <!-- Bars rather than a line: a gap in a line implies interpolation
                 across a day with no data, which is exactly the thing this
                 dataset exists to be honest about. A missing bar is missing. -->
            <div class="bars">
              {#each points as p (p.day)}
                <div
                  class="bar"
                  style="height:{peak > 0 && p.value != null ? Math.max((p.value / peak) * 100, 2) : 0}%"
                  title="{p.day}: {fmt(p.value, isNumeric ? unitOf : 'h')}"
                ></div>
              {/each}
            </div>
            <div class="axis sub">
              <span class="num">{points[0]?.day ?? ""}</span>
              <span class="num">peak {fmt(peak, isNumeric ? unitOf : "h")}</span>
              <span class="num">{points[points.length - 1]?.day ?? ""}</span>
            </div>

            <!-- This window against the one before it. A single series cannot
                 answer "is this getting worse" without you doing the arithmetic. -->
            {#if compare.data && !compare.loading}
              {@const c = compare.data}
              <div class="cmp">
                <div class="cmp__cell">
                  <div class="lb">this {c.days}d</div>
                  <div class="cmp__v">{isNumeric ? fmt(c.current.avg, unitOf) : fmt(c.current.onHours, "h")}</div>
                </div>
                <div class="cmp__cell">
                  <div class="lb">previous {c.days}d</div>
                  <div class="cmp__v sub">{isNumeric ? fmt(c.previous.avg, unitOf) : fmt(c.previous.onHours, "h")}</div>
                </div>
                <!-- {@const} must be the immediate child of a block, so the wrapper
                     is the {#if} rather than the div. -->
                {#if true}
                  {@const d = isNumeric ? c.change.avgPct : c.change.onHoursPct}
                  <div class="cmp__cell">
                    <div class="lb">change</div>
                    <!-- Glyph and sign as well as colour: amber for a rise, blue for
                         a fall, and neither is inherently good — that depends on
                         whether it is a pump or a solar yield. -->
                    <div class="cmp__v" class:up={d !== null && d > 0} class:down={d !== null && d < 0}>
                      {#if d !== null && d !== 0}<Icon name={d > 0 ? "alert" : "check"} size={12} />{/if}
                      {pct(d)}
                    </div>
                  </div>
                {/if}
              </div>
            {/if}

            <div class="acts acts--end">
              <button class="lnk" onclick={loadToday}>Today so far</button>
            </div>

            {#if showToday}
              {#if today.loading}
                <p class="sub pad">Loading…</p>
              {:else if today.error}
                <p class="sub pad"><Icon name="alert" size={12} /> {today.error}</p>
              {:else}
                <p class="sub foot">
                  <Icon name="alert" size={12} />
                  Today has no rollup by design — a partial day would read as a dip every morning.
                  This comes from raw, one entity, one partition.
                </p>
                <div class="scroll tbl">
                  <table>
                    <thead><tr><th>When</th><th>State</th><th>Value</th></tr></thead>
                    <tbody>
                      {#each today.data!.readings.slice(0, 40) as r (r.at)}
                        <tr>
                          <td class="sub num">{new Date(r.at).toLocaleTimeString("en-ZA", { timeZone: "Africa/Johannesburg", hour: "2-digit", minute: "2-digit" })}</td>
                          <td class="sub">{r.state ?? "—"}</td>
                          <td class="num">{r.num == null ? "—" : r.num}</td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {/if}
            {/if}
          {/if}
        </div>
      </div>
    {/if}
  </section>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card { padding: 13px 15px 15px; }
  .hero { padding: 22px 24px; }
  h1 { margin: 6px 0 4px; font-size: 1.5rem; font-weight: 800; letter-spacing: -0.6px; color: var(--tx); }
  .hero__p { max-width: 58ch; margin: 0; }
  .figs { display: flex; gap: 30px; margin-top: 18px; flex-wrap: wrap; }
  .fig .big { font-size: 1.875rem; }
  .silent { margin-top: 14px; }

  .sec { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding-bottom: 9px; }
  .acts { display: flex; align-items: center; gap: 6px; }
  .acts--end { justify-content: flex-end; padding-top: 8px; }
  .pad { padding: 6px 0 2px; display: flex; align-items: center; gap: 6px; }
  .foot { margin: 9px 0 0; display: flex; align-items: flex-start; gap: 6px; }
  code { font-size: 0.72rem; color: var(--acc); }

  .tab { padding: 3px 9px; border-radius: var(--r-pill); font-size: 0.72rem; font-weight: 700;
         color: var(--mut); box-shadow: inset 0 0 0 1px var(--line); }
  .tab--on { color: var(--tx); background: var(--wash); box-shadow: inset 0 0 0 1px var(--acc); }

  .search { width: 100%; margin-bottom: 10px; background: var(--fill); color: var(--tx);
            border: 1px solid var(--line); border-radius: var(--r-control); padding: 7px 9px; font-size: 0.7812rem; }

  .split { display: grid; grid-template-columns: minmax(200px, 280px) 1fr; gap: 14px; }
  @media (max-width: 800px) { .split { grid-template-columns: 1fr; } }

  .list { display: flex; flex-direction: column; gap: 1px; max-height: 380px; overflow-y: auto; }
  .ent { display: flex; align-items: center; justify-content: space-between; gap: 8px;
         padding: 6px 9px; border-radius: var(--r-control); text-align: left;
         font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .ent:hover { background: var(--card-hover); color: var(--tx); }
  .ent--on { background: var(--wash); color: var(--tx); box-shadow: inset 0 0 0 1px var(--acc); }
  .ent__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .ent__id { font-size: 0.72rem; color: var(--mut); opacity: 0.8; }
  .lb__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .lb__id { font-size: 0.72rem; color: var(--mut); opacity: 0.8; }
  .chart__id { margin-left: 8px; font-size: 0.72rem; }
  .ent__n { overflow-wrap: anywhere; }

  .chart { min-width: 0; }
  .chart__h { display: flex; align-items: baseline; gap: 10px; padding-bottom: 10px; flex-wrap: wrap; }
  .nm { font-weight: 700; color: var(--tx); font-size: 0.7812rem; }
  .bars { display: flex; align-items: flex-end; gap: 2px; height: 160px;
          padding: 8px; border-radius: var(--r-control); background: var(--fill);
          box-shadow: inset 0 0 0 1px var(--line); overflow-x: auto; }
  .bar { flex: 1; min-width: 3px; border-radius: 2px 2px 0 0; background: var(--ok); }
  .axis { display: flex; justify-content: space-between; padding: 5px 2px 0; gap: 10px; }

  .row { display: flex; gap: 9px; padding: 9px 10px; border-radius: var(--r-control);
         box-shadow: inset 0 0 0 1px var(--line); margin-bottom: 6px; align-items: flex-start; }
  .row--warn { border-left: 2px solid var(--warn); }
  .row__t { font-size: 0.7812rem; color: var(--tx); font-weight: 700; }
  .grow { flex: 1; min-width: 0; }

  .domains { display: flex; flex-wrap: wrap; gap: 6px; padding: 2px 0 10px; }
  .chip { padding: 3px 9px; border-radius: var(--r-pill); box-shadow: inset 0 0 0 1px var(--line);
          font-size: 0.72rem; font-weight: 700; color: var(--tx2); }
  .two { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  @media (max-width: 760px) { .two { grid-template-columns: 1fr; } }
  .colhead { font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.4px;
             padding-bottom: 4px; }
  .lb__row { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%;
             padding: 5px 8px; border-radius: var(--r-control); text-align: left;
             font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .lb__row:hover { background: var(--card-hover); color: var(--tx); }
  .lb__n { overflow-wrap: anywhere; }

  .cmp { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 12px;
         padding: 10px 12px; border-radius: var(--r-control); box-shadow: inset 0 0 0 1px var(--line); }
  .cmp__v { font-size: 0.9375rem; font-weight: 800; color: var(--tx); display: flex; align-items: center; gap: 5px; }
  .cmp__v.up { color: var(--warn); }
  .cmp__v.down { color: var(--ok); }

  .tbl { margin-top: 8px; }
  .scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.7812rem; }
  th { text-align: left; font-size: 0.72rem; font-weight: 700; color: var(--mut); padding: 5px 8px;
       border-bottom: 1px solid var(--line); white-space: nowrap; }
  td { padding: 6px 8px; border-bottom: 1px solid var(--line); }
  tbody tr:last-child td { border-bottom: none; }
  .lnk { font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
</style>
